/**
 * Small caps, `^^text^^` (decisions 35, 36). Delimiters are runs of exactly two unescaped carets; a longer
 * run is literal text. Whether a run can open or close follows CommonMark's flanking rules for `*` (as
 * Lezer's GFM strikethrough does for `~~`), so small caps nest with emphasis both ways and `up^^ward`
 * alone never opens anything that isn't closed.
 */
import type { DelimiterType, InlineContext, MarkdownConfig } from '@lezer/markdown';
import { CARET, isEscaped } from './chars.ts';

const SMALL_CAPS: DelimiterType = { resolve: 'SmallCaps', mark: 'SmallCapsMark' };

/**
 * `^`, read once: the parse below compares it at every position of a chapter's inline content, and under Vitest each read
 * of an imported binding is a getter call (4 ms of the notes bench's 25, its "module export getters" warning).
 */
const CARET_CODE = CARET;

/** Unicode punctuation or symbol, as CommonMark's flanking rules use it. */
const PUNCTUATION = /[\p{P}\p{S}]/u;
/** Whitespace, or the start or end of the inline content. */
const SPACE_OR_EDGE = /\s|^$/u;

/** Whether the run of carets at `pos` in the inline content is exactly two long. */
function isPair(cx: InlineContext, pos: number): boolean {
  if (cx.char(pos) !== CARET || cx.char(pos + 1) !== CARET || cx.char(pos + 2) === CARET) return false;
  // Before the text's start this reads NaN, which is no caret.
  const before = pos - cx.offset - 1;
  return !(cx.text.charCodeAt(before) === CARET && !isEscaped(cx.text, before));
}

/** The extension. */
export const smallCaps: MarkdownConfig = {
  defineNodes: ['SmallCaps', 'SmallCapsMark'],
  parseInline: [
    {
      name: 'SmallCaps',
      parse(cx: InlineContext, next: number, pos: number): number {
        // Lezer offers every position of the inline content: most are no caret, known without a call.
        if (next !== CARET_CODE || !isPair(cx, pos)) return -1;
        const before = cx.slice(pos - 1, pos);
        const after = cx.slice(pos + 2, pos + 3);
        const sBefore = SPACE_OR_EDGE.test(before);
        const sAfter = SPACE_OR_EDGE.test(after);
        const pBefore = PUNCTUATION.test(before);
        const pAfter = PUNCTUATION.test(after);
        const open = !sAfter && (!pAfter || sBefore || pBefore);
        const close = !sBefore && (!pBefore || sAfter || pAfter);
        return cx.addDelimiter(SMALL_CAPS, pos, pos + 2, open, close);
      },
    },
  ],
};
